import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Car, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [searchParams] = useSearchParams();
  const defaultType = searchParams.get('type') || 'rider';
  const [userType, setUserType] = useState(defaultType);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password, userType);
      toast.success('Welcome back!');
      navigate(userType === 'driver' ? '/driver' : '/rider');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1765208269671-4cc3bd040826?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHx1cmJhbiUyMGNpdHklMjBzdHJlZXQlMjBuaWdodCUyMHJlZCUyMHRhaWxsaWdodHMlMjBtb3Rpb24lMjBibHVyfGVufDB8fHx8MTc2ODUwOTg5OXww&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-slate-900/90"></div>
      
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
            <Car className="w-7 h-7 text-white" />
          </div>
          <span className="text-3xl font-display font-bold text-white tracking-tight">REDDI</span>
        </Link>

        {/* Login Card */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">
          <h1 className="text-2xl font-display font-bold text-white uppercase tracking-tight text-center mb-6">
            Welcome Back
          </h1>

          <Tabs value={userType} onValueChange={setUserType} className="mb-6">
            <TabsList className="grid w-full grid-cols-2 bg-white/10">
              <TabsTrigger 
                value="rider" 
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
                data-testid="rider-tab"
              >
                Rider
              </TabsTrigger>
              <TabsTrigger 
                value="driver"
                className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
                data-testid="driver-tab"
              >
                Driver
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-red-500 focus:ring-red-500/20"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:border-red-500 focus:ring-red-500/20"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider transition-all duration-300"
              data-testid="login-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                `Log in as ${userType === 'driver' ? 'Driver' : 'Rider'}`
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-slate-400">
            Don't have an account?{' '}
            <Link 
              to={`/register?type=${userType}`} 
              className="text-red-500 hover:text-red-400 font-semibold transition-colors"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
